// The review screen's field view: one frame of the trace, at the transport's current
// tick (`client-rounds-review`), drawn by the **shipped PixiJS world renderer**.
//
// It renders whichever tier is available for that position, which is what makes the tier
// indicator honest rather than decorative:
//
//   * **Detail** — the tick's record is resident: individual bees, flower patches, hive
//     cells and the hive itself, as sprites.
//   * **Coarse** — the tick's detail range is not resident (in flight, or the position
//     falls outside the detail span): the coarse tier's per-colony cluster blobs, the
//     same wide LOD tier a live match shows. **A real view, not a placeholder** — hence
//     no spinner, no stall, nothing implying something is wrong.
//
// **Sprites, through the one renderer** (closing **D-156**). This used to paint its own
// absolutely-positioned DOM dots, on the reasoning that a batch trace is not a wire
// stream and this screen has no session. That reasoning was wrong about the renderer: it
// wants a `WorldSnapshot`, a `Camera` and a `RenderInput` — all plain data — not a
// session and not a socket. So the review screen now shows the same pixel-art bees (with
// derived heading, palette-swapped bodies, health/nectar bars), the same cell-by-cell
// hive, the same flower sheets and nectar meters, and the same season ground as the
// Colony screen, because it is literally the same renderer. The mapping is the pure
// `ui/rounds/review-world.ts`; this file is only the host that owns the renderer's
// lifetime and pushes it a frame when the tick or the camera moves.
//
// The renderer factory is an **injectable seam with no default** (the `ColonyUnderlaySeams`
// precedent, not the `INERT_CONTROLLER` one): a real `CanvasRenderer` needs a GPU context,
// so a bare mount and every jsdom test render the frame chrome with no canvas rather than
// constructing Pixi. Production wires the factory in from the composition root.
//
// **The camera.** Marks used to be laid out as a percentage of the WHOLE field, which made
// the screen unusable for its actual subject: a sandbox run is one colony whose hive sits
// on the spawn ring near the field edge, so the hive rendered as a speck in a corner with
// its ~21 starting bees overlapped into one dot. The camera is now the screen's, driven by
// the same `{centre, zoom}` state and the same gestures the Match and Colony canvases use.

import { useEffect, useRef, type CSSProperties } from 'react';
import type { ReviewTrace } from '../../../net/rounds-review-source';
import { dequantizePosition } from '../../../net/rounds-trace';
import { Y_SQUASH, type Camera } from '../../../canvas/projection';
import type { RenderInput } from '../../../canvas/render-input';
import type { WorldSnapshot } from '../../../net/wire/connection';
import { Tier } from '../../../net/wire/messages';
import type { StateLabelPlayback, WideColonyContext } from '../../../canvas/renderer';
import {
  reviewRenderInput,
  reviewWideContext,
  reviewWorldSnapshot,
} from '../../rounds/review-world';

/**
 * The renderer surface this host drives. A real `CanvasRenderer` satisfies it; tests
 * inject a recorder. Deliberately a superset of `InMatchRenderer` by one parameter —
 * `wideColonyContext`, which colours coarse-tier blobs by colony — since the review
 * screen renders the wide tier as a first-class view rather than as a transient
 * degradation, and a blob with no colony colour would be the wrong picture.
 */
export interface ReviewRenderer {
  /** `signal` cancels a superseded construction (D-290) — a cancelled `init()` reclaims what it
   * has built and RESOLVES, so cancellation is never reported as a start-up failure. Optional,
   * which is what keeps every existing double that declares `init(): Promise<void>` assignable. */
  init(signal?: AbortSignal): Promise<void>;
  render(
    world: WorldSnapshot,
    camera: Camera,
    elapsedMs: number,
    tierChanged: boolean,
    renderInput?: RenderInput,
    wideColonyContext?: WideColonyContext,
  ): void;
  updateRenderInput(renderInput: RenderInput): void;
  /** The state-label layer's playback-side signals (WAG-36): the review subject colony, the
   * scrub flag, a one-frame seek pulse, and the trace's appears-later lookup. Optional so the
   * doubles that predate the layer still satisfy the seam; `CanvasRenderer` implements it. */
  setStateLabelPlayback?(playback: StateLabelPlayback): void;
  /** Picks (or clears) the bee labelled continuously and never suppressed. */
  setPickedBee?(beeId: number | null): void;
  /** The picked bee, or `null` — read back after a frame, since a pick whose bee has left the
   * trace clears itself. */
  readonly pickedBeeId?: number | null;
  /** Releases the label textures and the layer's baselines — on trace load and version switch. */
  invalidateStateLabels?(): void;
  destroy(): void;
}

/**
 * The colony's hive centre in world units, read off the hive **cells** at `tick` — the
 * only thing in a trace that marks where a colony lives (bees move; cells do not).
 * `null` when the tick carries no detail record or the colony has no cells left, which
 * the caller reads as "no colony anchor, fall back to the field centre".
 *
 * `colony` is a required argument, not a defaulted one: the old `REVIEW_COLONY = 0`
 * constant this replaced (`rounds-client` **D3**) made every caller silently mean colony 0,
 * which is exactly the "sandbox run is one colony alone" assumption a graded round breaks —
 * a graded trace holds every participant, and a default here would anchor the camera on
 * colony 0 regardless of which colony the identity line actually names.
 */
export function hiveCentreOf(
  trace: ReviewTrace,
  tick: number,
  colony: number,
): [number, number] | null {
  const detail = trace.detailAt(tick);
  if (detail === null) {
    return null;
  }
  const cells = detail.cells.filter((c) => c.colony === colony);
  if (cells.length === 0) {
    return null;
  }
  let sumX = 0;
  let sumY = 0;
  for (const cell of cells) {
    sumX += dequantizePosition(cell.position[0], trace.fieldSize);
    sumY += dequantizePosition(cell.position[1], trace.fieldSize);
  }
  return [sumX / cells.length, sumY / cells.length];
}

/**
 * How much wider than the colony's own extent the opening shot is framed.
 *
 * **A committed value, not a tuning knob left at whatever felt right.** It is a multiple of the
 * colony's extent rather than a distance in world units, so the framing follows the colony's
 * geometry and survives a change to the size of the field it sits in. 2.5 sits between two
 * failure modes: at the shipped 30-cell starting hive (extent 50.67 x 55.43 u about its centroid)
 * and the 390 px reference frame it yields a zoom of **3.08** — above the `SPRITE_NATIVE_ZOOM = 2`
 * the bee art is authored to read at, with the colony spanning 40% of the frame's width — while
 * still leaving about three bee sense radii (`bee.sense_radius_u = 40`) of surrounds in shot, so
 * departing foragers and the near grove stay visible rather than the frame closing to the comb
 * alone. Changing it reframes the game, so change it as a decision.
 */
export const COLONY_FIT_PADDING = 2.5;

/**
 * The zoom — screen pixels per world unit, the render `Camera.scale` axis — at which `colony`'s
 * own hive cells at `tick`, padded by {@link COLONY_FIT_PADDING}, fit `viewport` on both axes,
 * clamped into `bounds`. `hiveCentreOf`'s companion: that one says where the shot points, this
 * one says how close it stands, and the two read the same cells for the same reason — cells are
 * the only fixed marker of where a colony lives, so framing on bees would make the opening shot
 * depend on what happened to be in flight.
 *
 * The extent is measured **about the centroid the camera centres on** — `2 x max|cell - centroid|`
 * per axis — and deliberately **not** as a bounding box. A partially filled hive lattice's box
 * centre is not its centroid (the shipped starting hive's box is 51.96 u tall while its extent
 * about the centroid is 55.43 u), so a box-derived fit describes a shot the camera does not take
 * and under-frames by 6.7% on that axis.
 *
 * The vertical fit accounts for the projection's `Y_SQUASH`, since a world-space extent covers
 * fewer screen pixels vertically than horizontally, and the tighter of the two axes wins so the
 * padded extent is covered on both.
 *
 * `null` — never a `NaN`, an `Infinity`, a zero or a negative — when there is no colony to fit:
 * no detail record at the tick, no cells for `colony`, cells with zero extent on either axis (a
 * single cell, or a collinear pair), or a viewport that has not been laid out. The caller turns
 * that into its own fallback, so falling back is a decision rather than an accident.
 */
export function colonyFitZoom(
  trace: ReviewTrace,
  tick: number,
  colony: number,
  viewport: { width: number; height: number },
  bounds: { minScale: number; maxScale: number },
): number | null {
  if (
    !Number.isFinite(viewport.width) ||
    !Number.isFinite(viewport.height) ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return null;
  }
  const detail = trace.detailAt(tick);
  if (detail === null) {
    return null;
  }
  const cells = detail.cells.filter((c) => c.colony === colony);
  if (cells.length === 0) {
    return null;
  }
  const xs = cells.map((c) => dequantizePosition(c.position[0], trace.fieldSize));
  const ys = cells.map((c) => dequantizePosition(c.position[1], trace.fieldSize));
  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < cells.length; i += 1) {
    sumX += xs[i]!;
    sumY += ys[i]!;
  }
  const centreX = sumX / cells.length;
  const centreY = sumY / cells.length;
  let deviationX = 0;
  let deviationY = 0;
  for (let i = 0; i < cells.length; i += 1) {
    deviationX = Math.max(deviationX, Math.abs(xs[i]! - centreX));
    deviationY = Math.max(deviationY, Math.abs(ys[i]! - centreY));
  }
  const paddedX = 2 * deviationX * COLONY_FIT_PADDING;
  const paddedY = 2 * deviationY * COLONY_FIT_PADDING;
  if (paddedX <= 0 || paddedY <= 0) {
    return null;
  }
  const fit = Math.min(viewport.width / paddedX, viewport.height / (paddedY * Y_SQUASH));
  if (!Number.isFinite(fit) || fit <= 0) {
    return null;
  }
  return Math.min(bounds.maxScale, Math.max(bounds.minScale, fit));
}

/**
 * The zoom at which the **whole field** — `trace.fieldSize` square, centred at the origin
 * (`dequantizePosition`'s own range) — fits `viewport` on both axes, clamped into `bounds`. The
 * spectate camera's default (`tournament-big-screen` **D12**): the field is the spectacle, not
 * one colony's hive, so this is `colonyFitZoom`'s whole-field sibling rather than a fit derived
 * from any one colony's cells. `null` only for a viewport that has not been laid out — the field's
 * own size is always known once a trace is open, so there is no "nothing to fit" case here.
 */
export function fieldFitZoom(
  trace: ReviewTrace,
  viewport: { width: number; height: number },
  bounds: { minScale: number; maxScale: number },
): number | null {
  if (
    !Number.isFinite(viewport.width) ||
    !Number.isFinite(viewport.height) ||
    viewport.width <= 0 ||
    viewport.height <= 0 ||
    trace.fieldSize <= 0
  ) {
    return null;
  }
  const fit = Math.min(viewport.width / trace.fieldSize, viewport.height / (trace.fieldSize * Y_SQUASH));
  if (!Number.isFinite(fit) || fit <= 0) {
    return null;
  }
  return Math.min(bounds.maxScale, Math.max(bounds.minScale, fit));
}

/**
 * Whether `colony` is part of the trace's own roster at `tick` — checked against whichever
 * tier answers (a trace's colony roster is fixed for its whole span, so either tier's own
 * per-colony rows settle the question, and `reviewWideContext` already derives the same list
 * for the wide-tier colouring context). Neither tier answering yet is **not** evidence of
 * absence: it is the opening instant of a fresh open, before even the coarse tier — fetched
 * whole at open — has been read once, and must never be read as "this colony does not exist".
 *
 * This is the check `ReviewFrame` uses to refuse the exact hazard **D3** names: a subject the
 * trace does not contain must render an absent state, never colony 0's entities under the
 * requested colony's identity.
 */
function subjectPresentIn(trace: ReviewTrace, tick: number, colony: number): boolean {
  const detail = trace.detailAt(tick);
  if (detail !== null) {
    return detail.colonies.some((c) => c.colony === colony);
  }
  const coarse = trace.coarseAt(tick);
  if (coarse !== null) {
    return coarse.colonies.some((c) => c.colony === colony);
  }
  return true;
}

export interface ReviewFrameProps {
  trace: ReviewTrace;
  /** The transport's current tick. */
  tick: number;
  /** The camera the frame is seen through, derived by the screen from its measured size. */
  camera: Camera;
  /** The measured pixel size of the frame. Held so a resize redraws immediately rather
   * than waiting for the next tick. */
  viewport: { width: number; height: number };
  /** The colony's hive anchor in world units — the hive tile and the own queen. */
  hive: readonly [number, number] | null;
  /**
   * Which colony this frame is watching (**G8**'s subject selector) — **required**, never
   * defaulted. The deleted `REVIEW_COLONY = 0` constant this prop replaces made every frame
   * silently mean colony 0; a caller must now always say which colony it means, so a subject
   * the trace does not contain can be told apart from colony 0 rather than quietly rendered
   * as it (**D3**).
   */
  subjectColony: number;
  /**
   * WAG-36 — the playhead is being **dragged**. Crowd labels are suppressed for its duration
   * (a picked bee's is not), and nothing seen during the drag is emitted after it.
   */
  scrubbing?: boolean;
  /**
   * WAG-36 — bumped by the screen on every **discontinuous** playhead jump. The value itself
   * means nothing; a *change* raises the layer's one-frame seek pulse, which re-baselines
   * silently so a jump does not fire a mass flash of transitions that happened during it.
   */
  seekNonce?: number;
  /** WAG-36 — the bee whose state is labelled continuously and never suppressed, or `null`.
   * Also the bee `reviewRenderInput` draws the outline silhouette for (bee-selection-outline,
   * `RenderInput.selectedBeeId`) — one pick, both the persistent label and the canvas outline. */
  pickedBeeId?: number | null;
  /** WAG-36 — the layer cleared the pick itself, because its bee left the trace. The screen owns
   * the pick, so it has to be told; without this the screen's state and the layer's disagree and
   * the next frame re-picks a bee that is gone. */
  onPickedBeeChange?: (beeId: number | null) => void;
  /** The world-renderer factory. Omitted (a bare mount, every jsdom test) renders the
   * frame chrome with no canvas rather than constructing a GPU-backed renderer. */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => ReviewRenderer;
}

const HOST_STYLE: CSSProperties = { position: 'absolute', inset: 0, overflow: 'hidden' };

// The absent state must **cover**, not merely coexist. The canvas host is a later sibling and both
// are `position: absolute`, so without an explicit stacking order the canvas wins — and the draw is
// *skipped* for an absent subject rather than cleared, so the renderer still holds the previously
// watched colony's frame. Arriving here by switching subjects would then show that colony's world
// beneath the "isn't part of this trace" message: colony 0's entities under another colony's
// identity, the exact picture **D3** exists to forbid. Hence a z-index above the host and an opaque
// fill — either alone would leave the stale frame showing. Asserted in `review-camera.test.tsx`
// ("covers the last drawn frame"), structurally, because jsdom does not paint.
const ABSENT_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  background: 'var(--hud-bg-solid)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  textAlign: 'center',
  color: 'var(--hud-text-dim)',
  fontFamily: 'var(--font-display)',
};

export function ReviewFrame({
  trace,
  tick,
  camera,
  viewport,
  hive,
  subjectColony,
  scrubbing = false,
  seekNonce = 0,
  pickedBeeId = null,
  onPickedBeeChange,
  createRenderer,
}: ReviewFrameProps) {
  const detail = trace.detailAt(tick);
  const coarse = detail === null ? trace.coarseAt(tick) : null;
  // The absent-subject rule (**D3**): a subject the trace's own roster does not carry at this
  // tick is reported as absent rather than rendered — never colony 0 under another colony's
  // name, because there is no other colony's data being substituted here at all.
  const present = subjectPresentIn(trace, tick, subjectColony);

  const hostRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<ReviewRenderer | null>(null);
  const lastFrameMsRef = useRef<number | null>(null);
  const lastTierRef = useRef<number | null>(null);
  // The latest inputs, read by the draw whenever it runs — held in a ref so the mount
  // effect never depends on them and a scrub therefore never rebuilds the renderer.
  const drawRef = useRef<() => void>(() => undefined);
  // WAG-36's three pieces of cross-frame bookkeeping.
  //
  // `pickTickRef` is what makes design **E9**'s "fate in the trace" test cost nothing. A bee's
  // life in a trace is ONE contiguous interval and the pick was made inside it, so absence at a
  // tick at or after the pick means the bee died (clear), and absence before it means the
  // playhead is behind the bee's birth (dormant). No new seam on `ReviewTrace`, no forward scan.
  const pickTickRef = useRef(0);
  /** The pick as last pushed INTO the renderer, so a pick the layer cleared for itself is
   * reported up rather than re-pushed on the next frame. */
  const pushedPickRef = useRef<number | null>(null);
  const lastSeekNonceRef = useRef(seekNonce);

  // The draw, in two halves (**D-291**).
  //
  // **The inner half performs the frame and commits its cross-frame bookkeeping only once every
  // renderer interaction has returned.** It used to write each value as it was derived — before
  // the call that consumes it — which was invisible while a draw could not fail. Now that a
  // throwing draw is contained and retried, it matters: every one of these is a ONE-SHOT EDGE
  // SIGNAL, so committing it against a frame that never reached the screen does not delay it, it
  // destroys it. `lastTierRef` would leave the next draw reading `tierChanged === false` and skip
  // the tier crossfade entirely; `lastSeekNonceRef` would swallow the one-frame seek pulse, so
  // the label layer re-baselines against a frame that did not happen; `pushedPickRef` written
  // before `setPickedBee` would lose the push for good if that call was the one that threw. The
  // containment below would then be honest about the exception and quietly wrong about the
  // picture — "the next draw recovers, minus exactly the signals that mattered".
  //
  // Returns the pick to report UP to the screen, or `null`. Deliberately a return value rather
  // than an inline call: `onPickedBeeChange` is the screen's callback, not a renderer
  // interaction, and must not sit inside the renderer's error boundary (see the outer half).
  const draw = (): { pickedBee: number | null } | null => {
    const renderer = rendererRef.current;
    // A torn-down run nulls `rendererRef`, so the null check IS the disposal guard — there is
    // deliberately no separate disposed flag to fall out of step with it.
    if (renderer === null || !present) {
      // An absent subject never reaches the renderer with a frame — the absent-state
      // markup below is the whole of what renders for it.
      return null;
    }
    const world = reviewWorldSnapshot(trace, tick);
    if (world === null) {
      return null; // neither tier answers for this tick: leave the last frame up, never blank
    }
    const now = performance.now();
    const elapsedMs = lastFrameMsRef.current === null ? 0 : now - lastFrameMsRef.current;
    const tierChanged = lastTierRef.current !== null && lastTierRef.current !== world.tier;
    // WAG-36. Pushed before the frame the layer decides against, and only on a change, so the
    // pick the layer clears for itself is not immediately re-asserted by this side.
    const pushingPick = renderer.setPickedBee !== undefined && pickedBeeId !== pushedPickRef.current;
    if (pushingPick) {
      if (pickedBeeId !== null) {
        // The ONE ref still committed up-front, and deliberately: the `pickedBeeAppearsLater`
        // closure below reads it *during* the render call, so deferring it would hand the label
        // layer a stale tick. A failed draw can therefore re-stamp it one tick later on the
        // retry — a dormant/cleared boundary shifted by the width of one failed frame, which is
        // accepted best-effort rather than overlooked.
        pickTickRef.current = tick;
      }
      renderer.setPickedBee?.(pickedBeeId);
    }
    const seek = seekNonce !== lastSeekNonceRef.current;
    renderer.setStateLabelPlayback?.({
      subjectColonyId: subjectColony,
      scrubbing,
      seek,
      // Design **E9**: a picked bee absent from the current frame is *dormant* if it is present
      // later in the trace and only *cleared* if it is gone from the remainder — the distinction
      // that stops one scrub drag from silently discarding the pick.
      //
      // A bee's life in a trace is one contiguous interval and the pick was made inside it, so
      // "before the pick tick" means the playhead is behind its birth and "at or after" means it
      // died — but **only where this frame is authoritative about who is alive**. It is not on the
      // coarse tier: `reviewWorldSnapshot` falls back to coarse whenever the detail range is
      // non-resident and returns an empty bee map by construction, so every bee reads as absent
      // there. Answering `false` in that case would clear the pick on cache residency rather than
      // on the bee's fate, permanently — clearing is a one-way event. Absence on a non-detail
      // frame therefore carries no information and is reported as dormant.
      pickedBeeAppearsLater: () => world.tier !== Tier.Close || tick < pickTickRef.current,
    });
    renderer.render(
      world,
      camera,
      elapsedMs,
      tierChanged,
      reviewRenderInput(trace, tick, hive, subjectColony, pickedBeeId),
      reviewWideContext(trace, tick),
    );
    // Every renderer interaction returned, so the frame really did happen and the bookkeeping
    // may now claim it did. Nothing above this line writes a ref that a later draw reads.
    lastFrameMsRef.current = now;
    lastTierRef.current = world.tier;
    if (pushingPick) {
      pushedPickRef.current = pickedBeeId;
    }
    lastSeekNonceRef.current = seekNonce;
    const settled = renderer.pickedBeeId;
    if (settled !== undefined && settled !== pushedPickRef.current) {
      pushedPickRef.current = settled;
      return { pickedBee: settled };
    }
    return null;
  };

  // **The outer half: containment (D-291).**
  //
  // The guard lives HERE, wrapping every caller, rather than around the first draw alone. It used
  // to sit in the mount effect's fulfilment handler, which covered the opening frame and nothing
  // else — leaving the playback effect below (which fires on tick, camera, viewport, trace,
  // subject, scrub, seek and pick) to throw straight out into React's effect flush. That is every
  // draw after the first.
  //
  // A throw out of a draw is a *drawing* failure, not a start-up failure. The renderer started
  // and stays published to `rendererRef`, so the next scrub still uses it: same fail-open posture
  // as a rejecting `init()`, leaving the frame chrome, the transport and the score readout intact.
  drawRef.current = () => {
    let notify: { pickedBee: number | null } | null = null;
    try {
      notify = draw();
    } catch (error: unknown) {
      console.error('waggle: the review frame renderer failed to draw a frame', error);
      return;
    }
    if (notify === null) {
      return;
    }
    // Outside the renderer's error boundary on purpose. This is the SCREEN's callback, not a
    // renderer interaction, and folding it in would report a bug in the screen's own pick handler
    // as "the renderer failed to draw a frame" — pointing every future debugger at Pixi. It still
    // gets contained, just under its own name.
    try {
      onPickedBeeChange?.(notify.pickedBee);
    } catch (error: unknown) {
      console.error('waggle: the review frame pick handler threw', error);
    }
  };

  // A new trace is a new state-name table and a new set of bee ids, so neither the cached label
  // textures nor the layer's baselines carry over (design **E8**). Without this the texture key
  // space is per-session rather than per-run, and a sandbox session iterating a dozen Runs
  // accumulates a texture for every name it has ever drawn. Declared before the draw effect so a
  // trace change invalidates and then redraws, never the other way round.
  useEffect(() => {
    rendererRef.current?.invalidateStateLabels?.();
    pushedPickRef.current = null;
  }, [trace]);

  // Own the renderer's lifetime. Keyed on the factory alone, so nothing about playback —
  // not a tick, not a pan, not a zoom — tears the Pixi application down and rebuilds it.
  //
  // Disposal is a per-invocation LOCAL and the cleanup destroys ITS OWN `built` — never a
  // shared ref (WAG-31/WAG-32). `init()` is genuinely async and appends the canvas only when
  // it resolves, so under StrictMode's mount → cleanup → remount a ref-based flag gets reset
  // by the second run before either init settles: the first construction survives its own
  // cleanup, both canvases land in the host, and the live one is clipped below the stale one.
  // The regression lives in `review-camera.test.tsx` ("owns exactly one renderer").
  useEffect(() => {
    const host = hostRef.current;
    if (host === null || createRenderer === undefined) {
      return undefined;
    }
    // Cancellation is **per run**, captured by this closure. It was previously a component-level
    // ref, which cannot tell "this run was superseded" from "a later run is alive": the next run
    // reset the shared flag, so a superseded run whose async `init()` settled last both escaped
    // destruction and overwrote `rendererRef` with itself. That left a second, orphaned canvas in
    // the host — a leaked Pixi application and WebGL context — and pointed every subsequent frame
    // at it, which is a world frozen on its first frame.
    let cancelled = false;
    // The renderer's own cancellation channel (**D-290**), per run exactly as `cancelled` is.
    //
    // `cancelled` tells THIS closure that it was superseded; the controller tells the RENDERER,
    // which is the only party that can reclaim a canvas and a GPU context it has not finished
    // building yet. Without it, a run cancelled inside `init()` kept both alive until the sprite
    // load settled — and for ever if it hung, since a hung load never settles to be collected.
    const controller = new AbortController();
    // Teardown is **immediate and exactly-once**, whatever stage `init()` has reached.
    //
    // This used to be an `initialised` flag with the cleanup running `if (initialised)`, on the
    // reasoning that teardown must never touch a half-constructed renderer. That left the real
    // leak open: `CanvasRenderer.init()` appends its canvas and only *then* awaits
    // `assets.load()`, so a superseded run owns an attached canvas and a live WebGL context for
    // the whole of that await while `initialised` is still false — and the guard reclaimed
    // nothing. The orphan survived until its asset load settled, or indefinitely if it hung,
    // while the next run initialised beside it: two canvases in one host, which is the WAG-31/32
    // symptom reached by a slower route (implementation review,
    // `beekeeper-chat-markdown-tables`).
    //
    // Destroying early is safe because `CanvasRenderer.destroy()` was already built for it: it
    // guards on `this.app.renderer` — the field pixi assigns as `init()`'s first step and nulls
    // on destroy — so it is both partial-init tolerant and **idempotent**
    // (`client-rendering`: "`init`/`destroy` MUST be idempotent").
    //
    // It is deliberately NOT latched to a single call, and that is the subtle half. An early
    // teardown reclaims only what exists *at that moment*: a run cancelled while still inside
    // `await this.app.init(...)` has no canvas to remove yet, and `init()` then carries on and
    // appends one. So teardown runs twice on the cancelled path — once at cleanup, to give back a
    // context that is already live, and once when `init()` finally settles, to collect anything
    // it attached afterwards. Idempotence is what makes the second call free. Latching it to one
    // call reopens the leak from the other end.
    const teardown = (): void => {
      try {
        built.destroy();
      } catch (teardownError: unknown) {
        console.error('waggle: the review frame renderer failed to tear down', teardownError);
      }
    };
    // **trace-required** (client-tick-rate-provenance, tick-rate-single-source E4): sourced
    // from the trace's own `tickRateHz` (the trace-manifest response envelope's rate) — no
    // defaulted argument. `trace` is a required prop, so this is always available.
    const built = createRenderer(host, trace.tickRateHz);
    void built.init(controller.signal).then(
      () => {
        if (cancelled) {
          // Ordinarily already reclaimed — twice over: the cleanup aborted the controller, and a
          // renderer that honours the signal has torn itself down before resolving. Kept because
          // it is free (`destroy()` is idempotent) and because this seam accepts doubles that
          // ignore the signal entirely, the parameter being optional. Removing it is gated on the
          // live-mode freeze fix landing (D-290's remedy, second half).
          teardown();
          return;
        }
        rendererRef.current = built;
        // Unguarded on purpose: `drawRef.current` now contains its own failures (D-291), so every
        // caller inherits the guard rather than the first draw alone owning one. This handler
        // deliberately still does not wrap anything — a trailing `.catch` on the chain could not
        // tell a draw failure from the cancelled path's `built.destroy()` throwing, and would
        // report both as a draw failure. Each handler stays responsible for its own failure.
        drawRef.current();
      },
      // A renderer that cannot start (no WebGL context) leaves the frame chrome, the
      // transport and the score readout intact rather than taking the screen down with
      // it — and says so once, instead of surfacing as an unhandled rejection.
      //
      // **It is still torn down.** A rejection is not "nothing was built": the shipped
      // `CanvasRenderer.init()` appends its canvas and only *then* awaits `assets.load()`, so a
      // failed sheet load rejects with a live canvas already in the host. Since a failed init
      // never publishes to `rendererRef`, the cleanup's ownership guard would never collect it —
      // leaving exactly the orphan canvas WAG-31/32 was (artifacts review F4): `initialised`
      // never becomes true on this path, so the cleanup's `if (initialised)` guard would never
      // run it either. `destroy()` runs in its own guard because a half-built renderer may throw
      // from it, and that must not mask the real error.
      //
      // This is the **two-argument** `then`, not a trailing `.catch`: a chained catch also
      // catches a throw from the fulfilment handler above, which would destroy a renderer
      // already published to `rendererRef` — leaving the screen holding a live reference to a
      // destroyed renderer, and the cleanup destroying it a second time. Only an `init()`
      // rejection is an init failure.
      (error: unknown) => {
        teardown();
        console.error('waggle: the review frame renderer failed to start', error);
      },
    );
    return () => {
      cancelled = true;
      // Tell the renderer first: a construction still in flight reclaims its canvas and its GPU
      // context AT ONCE on this, rather than when the sprite load happens to settle.
      controller.abort();
      lastFrameMsRef.current = null;
      lastTierRef.current = null;
      // Only ever relinquish the ref if THIS run is the one holding it — a later run's
      // renderer must survive this run's teardown.
      if (rendererRef.current === built) {
        rendererRef.current = null;
      }
      // Unconditional: a run torn down mid-`init()` must give its canvas and context back now.
      teardown();
    };
  }, [createRenderer]);

  // Push a frame whenever the position, the camera, the measured size or the watched
  // subject moves — and whenever WAG-36's playback signals do, since a scrub starting, a seek
  // landing or a pick changing must all be seen by the label layer on their own frame.
  //
  // `hive` and `present` are deliberately absent, and that is not an oversight: both are DERIVED
  // from values already listed. `hive` is the caller's `hiveCentreOf(trace, tick, colony) ??
  // homeCentre` and `present` is `subjectPresentIn(trace, tick, subjectColony)` — pure functions of
  // `trace`, `tick` and `subjectColony`, so neither can change without one of those changing.
  // Listing `hive` would also be worse than redundant: `hiveCentreOf` returns a fresh array on
  // every call, so the dep would never compare equal and this effect would fire on every render,
  // reading as a value-change trigger while actually being a render trigger.
  //
  // The camera is named by its three VALUES for that same reason. `SandboxScreen` builds it inline
  // (`cameraForZoomCentre(centre, zoom, viewport)` on every render, not memoized), so depending on
  // the object by reference fired this effect on renders that moved nothing — a chat message
  // arriving, the phase clock ticking — pushing a redundant full sync at whatever bee count the
  // run had reached. `Camera` is three flat numbers, so there is nothing to lose by comparing them.
  // `trace.extentVersion` is named for the same reason and against the opposite problem. A published
  // trace is immutable, so its identity is a complete growth signal; a run being **watched** grows in
  // place, so its identity is a complete *non*-signal. Naming both covers both: the reference catches
  // "a different trace entirely" and the version catches "the same trace, with more of the run in it"
  // — which is the case a paused watcher depends on, having no advancing tick to redraw for it. It is
  // `undefined` for every published trace, so this dep is inert wherever it cannot apply.
  useEffect(() => {
    drawRef.current();
  }, [
    tick,
    camera.offsetX,
    camera.offsetY,
    camera.scale,
    viewport.width,
    viewport.height,
    trace,
    trace.extentVersion,
    subjectColony,
    scrubbing,
    seekNonce,
    pickedBeeId,
  ]);

  return (
    <div
      data-testid="review-frame"
      data-tick={tick}
      data-tier={detail !== null ? 'detail' : 'coarse'}
      data-bees={detail !== null ? detail.bees.length : undefined}
      data-patches={detail !== null ? detail.patches.length : undefined}
      data-blobs={detail === null ? (coarse?.clusters.length ?? 0) : undefined}
      data-subject-absent={present ? undefined : 'true'}
      style={HOST_STYLE}
    >
      {!present && (
        // The absent-subject state (**D3**): a normal result, not a failure — the trace is
        // healthy, this particular colony simply is not (or is no longer) part of it.
        <div data-testid="review-subject-absent" role="status" style={ABSENT_STYLE}>
          This colony isn’t part of this trace.
        </div>
      )}
      {createRenderer !== undefined && (
        <div ref={hostRef} data-testid="review-canvas-host" style={HOST_STYLE} />
      )}
    </div>
  );
}
